'use client';
import { SimplePaletteColorOptions, createTheme, Theme } from '@mui/material/styles';
import { TypographyStyleOptions } from '@mui/material/styles/createTypography';
import taiwindConfig from '../../../tailwind.config';
import { CustomExtendConfig } from '../../../tailwind.type';
import { fontSize } from '@mui/system';

declare module '@mui/material/styles' {
  interface TypeAction {
    divider?: string;
  }
}

const tc: any = taiwindConfig;
const extendConfig: CustomExtendConfig = tc.theme?.extend as CustomExtendConfig;

declare module '@mui/material/styles' {
  interface Theme {
    customShadows: { default: string };
  }

  interface ThemeOptions {
    customShadows: { default: string };
  }
  interface Palette {
    complementary: SimplePaletteColorOptions;
    placeholder: SimplePaletteColorOptions;
    borders: SimplePaletteColorOptions;
    title: SimplePaletteColorOptions;
    paragraph: SimplePaletteColorOptions;
    overlay: SimplePaletteColorOptions & { opacity: number };
    neutral: SimplePaletteColorOptions;
    paper: SimplePaletteColorOptions;
    actions?: SimplePaletteColorOptions;
    divider: string;
    gradient: SimplePaletteColorOptions;
  }
  interface PaletteOptions {
    complementary: SimplePaletteColorOptions;
    placeholder: SimplePaletteColorOptions;
    borders: SimplePaletteColorOptions;
    title: SimplePaletteColorOptions;
    neutral: SimplePaletteColorOptions;
    paragraph: SimplePaletteColorOptions;
    overlay: SimplePaletteColorOptions & { opacity: number };
    paper: SimplePaletteColorOptions;
    actions?: SimplePaletteColorOptions;
    gradient: SimplePaletteColorOptions;
  }
  interface TypographyVariants {
    'title-xxl': TypographyStyleOptions;
    'title-xl': TypographyStyleOptions;
    h1: TypographyStyleOptions;
    'work-sans-h1': TypographyStyleOptions;
    'title-mac-sans-2xl': TypographyStyleOptions;
    'title-mac-sans-xl': TypographyStyleOptions;
    h2: TypographyStyleOptions;
    'mac-sans-h2': TypographyStyleOptions;
    h3: TypographyStyleOptions;
    'mac-sans-h3': TypographyStyleOptions;
    h4: TypographyStyleOptions;
    'mac-sans-h4': TypographyStyleOptions;
    h5: TypographyStyleOptions;
    'mac-sans-h5': TypographyStyleOptions;
    s1: TypographyStyleOptions;
    s2: TypographyStyleOptions;
    'body-500': TypographyStyleOptions;
    'body-400': TypographyStyleOptions;
    'title-cta': TypographyStyleOptions;
    'title-small': TypographyStyleOptions;
    'paragraph-big': TypographyStyleOptions;
    'paragraph-small': TypographyStyleOptions;
    'body-small-500': TypographyStyleOptions;
    'body-small-400': TypographyStyleOptions;
    'caption-500': TypographyStyleOptions;
    'caption-400': TypographyStyleOptions;
    'caption-small-500': TypographyStyleOptions;
    'caption-small': TypographyStyleOptions;
    'button-xl': TypographyStyleOptions;
    'button-l': TypographyStyleOptions;
    'button-m': TypographyStyleOptions;
    'button-s': TypographyStyleOptions;
    placeholder: TypographyStyleOptions;
    details: TypographyStyleOptions;
  }

  interface TypographyVariantsOptions {
    'title-xxl': TypographyStyleOptions;
    'title-xl': TypographyStyleOptions;
    h1: TypographyStyleOptions;
    'work-sans-h1': TypographyStyleOptions;
    'title-mac-sans-2xl': TypographyStyleOptions;
    'title-mac-sans-xl': TypographyStyleOptions;
    h2: TypographyStyleOptions;
    'mac-sans-h2': TypographyStyleOptions;
    h3: TypographyStyleOptions;
    'mac-sans-h3': TypographyStyleOptions;
    h4: TypographyStyleOptions;
    'mac-sans-h4': TypographyStyleOptions;
    h5: TypographyStyleOptions;
    'mac-sans-h5': TypographyStyleOptions;
    s1: TypographyStyleOptions;
    s2: TypographyStyleOptions;
    'body-500': TypographyStyleOptions;
    'body-400': TypographyStyleOptions;
    'title-cta': TypographyStyleOptions;
    'title-small': TypographyStyleOptions;
    'paragraph-big': TypographyStyleOptions;
    'paragraph-small': TypographyStyleOptions;
    'body-small-500': TypographyStyleOptions;
    'body-small-400': TypographyStyleOptions;
    'caption-500': TypographyStyleOptions;
    'caption-400': TypographyStyleOptions;
    'caption-small-500': TypographyStyleOptions;
    'caption-small': TypographyStyleOptions;
    'button-xl': TypographyStyleOptions;
    'button-l': TypographyStyleOptions;
    'button-m': TypographyStyleOptions;
    'button-s': TypographyStyleOptions;
    placeholder: TypographyStyleOptions;
    details: TypographyStyleOptions;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    'title-xxl': true;
    'title-xl': true;
    h1: true;
    'work-sans-h1': true;
    'title-mac-sans-2xl': true;
    'title-mac-sans-xl': true;
    h2: true;
    'mac-sans-h2': true;
    h3: true;
    'mac-sans-h3': true;
    h4: true;
    'mac-sans-h4': true;
    h5: true;
    'mac-sans-h5': true;
    s1: true;
    s2: true;
    'body-500': true;
    'body-400': true;
    'title-cta': true;
    'title-small': true;
    'paragraph-big': true;
    'paragraph-small': true;
    'body-small-500': true;
    'body-small-400': true;
    'caption-500': true;
    'caption-400': true;
    'caption-small-500': true;
    'caption-small': true;
    'button-xl': true;
    'button-l': true;
    'button-m': true;
    'button-s': true;
    placeholder: true;
    details: true;
  }
}

const theme = createTheme({
  palette: {
    success: {
      light: extendConfig.colors?.success.light,
      main: extendConfig.colors?.success.main,
      dark: extendConfig.colors?.success.dark,
    },
    primary: {
      light: extendConfig.colors?.primary.light,
      main: extendConfig.colors?.primary.main, // Colore primario
      dark: extendConfig.colors?.primary.dark,
    },
    secondary: {
      light: extendConfig.colors?.secondary.light,
      main: extendConfig.colors?.secondary.main,
      dark: extendConfig.colors?.secondary.dark,
      contrastText: extendConfig.colors?.secondary.contrastText,
    },
    complementary: {
      light: extendConfig.colors?.complementary.light,
      main: extendConfig.colors?.complementary.main,
      dark: extendConfig.colors?.complementary.dark,
      contrastText: extendConfig.colors?.complementary.contrastText,
    },
    error: {
      light: extendConfig.colors?.error.light,
      main: extendConfig.colors?.error.main,
      dark: extendConfig.colors?.error.dark,
    },
    text: {
      primary: extendConfig.colors?.text.primary,
      secondary: extendConfig.colors?.text.secondary,
      disabled: extendConfig.colors?.text.disabled,
    },
    paper: {
      main: extendConfig.colors?.paper,
    },
    action: {
      divider: extendConfig.colors?.action.divider,
      focus: extendConfig.colors?.action.focus,
    },
    background: {
      default: '#1C1D22', // Colore di sfondo
    },
    neutral: {
      main: '#54566870',
    },
    placeholder: {
      main: '#818084', // Placeholder
    },
    borders: {
      main: '#B8C9D7', // Borders
    },
    title: {
      main: '#34315F', // Titolo
    },
    paragraph: {
      main: '#434559', // Paragrafo
    },
    overlay: {
      main: '#34315F', // Overlay
      opacity: 0.7,
    },
    gradient: {
      light: extendConfig.colors?.gradient?.light || '#F3E8FF',
      main: extendConfig.colors?.gradient?.main || '#A855F7',
      dark: extendConfig.colors?.gradient?.dark || '#7B2CBF',
    },
  },

  typography: {
    fontFamily: `'Work Sans', sans-serif`,
    'title-xxl': {
      fontSize: 'clamp(4rem, 8vw, 8rem)', // Min: 64px, Max: 128px
      // fontSize: '8rem', // 128px
      fontWeight: 600,
      lineHeight: 'clamp(4.5rem, 8.5vw, 8.375rem)',
      // lineHeight: '8.375rem', // 134px
    },
    'title-xl': {
      // fontSize: '3.5rem', // 56px,
      fontSize: 'clamp(2rem, 5vw, 3.5rem)', // Min: 32px, Max: 56px
      fontWeight: 600,
      // lineHeight: '3.75rem', // 60px
      lineHeight: 'clamp(2.5rem, 5.5vw, 3.75rem)',
    },
    h1: {
      fontFamily: "'Mac Sans', sans-serif",
      fontSize: '3rem', // 48px
      fontWeight: 700,
      lineHeight: '3.625rem', // 58px
      '@media (max-width:640px)': {
        fontSize: '2rem', // 32px
        fontWeight: 700,
        lineHeight: '2.375rem', // 38px
      },
    },
    'work-sans-h1': {
      fontFamily: `'Work Sans', sans-serif`,
      fontSize: '3rem', // 48px
      fontWeight: 600,
      lineHeight: '3.0625rem', // 49px
      '@media (max-width:640px)': {
        fontSize: '2rem', // 32px
        fontWeight: 700,
        lineHeight: '2.375rem', // 38px
      },
    },
    'title-mac-sans-2xl': {
      fontFamily: "'Mac Sans', sans-serif",
      // fontSize: '9rem', // 144px
      fontSize: 'clamp(3.3rem, 10vw, 9rem)',
      fontWeight: 600,
      lineHeight: 'clamp(4.5rem, 10vw, 7.625rem)',
      // lineHeight: '7.625rem', // 134px
    },
    'title-mac-sans-xl': {
      fontFamily: "'Mac Sans', sans-serif",
      fontSize: 'clamp(2.1rem, 5vw, 3.5rem)', // Min: 40px, Max: 56px
      // fontSize: '3.5rem', // 56px,
      fontWeight: 600,
      lineHeight: 'clamp(2.2rem, 5.5vw, 3.75rem)',
    },
    h2: {
      fontSize: '2.5rem', // 40px
      fontWeight: 600,
      lineHeight: '3rem', // 48px
      '@media (max-width:640px)': {
        fontSize: '1.75rem', // 28px
        lineHeight: '2.125rem', // 34px
      },
    },
    'mac-sans-h2': {
      fontFamily: "'Mac Sans', sans-serif",
      fontSize: '2.5rem', // 40px
      fontWeight: 700,
      lineHeight: '3rem', // 48px
      '@media (max-width:640px)': {
        fontSize: '1.75rem', // 28px
        lineHeight: '2.125rem', // 34px
      },
    },
    h3: {
      fontFamily: `'Work Sans', sans-serif`,
      fontSize: '2rem', // 32px
      fontWeight: 600,
      lineHeight: '2.375rem', // 38px
      '@media (max-width:640px)': {
        fontSize: '1.5rem', // 24px
        lineHeight: '1.75rem', // 28px
      },
    },
    'mac-sans-h3': {
      fontFamily: "'Mac Sans', sans-serif",
      fontSize: '2rem', // 32px
      fontWeight: 700,
      lineHeight: '2.375rem', // 38px
      '@media (max-width:640px)': {
        fontSize: '1.5rem', // 24px
        lineHeight: '1.75rem', // 28px
      },
    },
    h4: {
      fontSize: '1.75rem', // 28px
      fontWeight: 600,
      lineHeight: '2.125rem', // 34px
      '@media (max-width:640px)': {
        fontSize: '1.375rem', // 22px
        lineHeight: '1.625rem', // 26px
      },
    },
    'mac-sans-h4': {
      fontFamily: "'Mac Sans', sans-serif",
      fontSize: '1.75rem', // 28px
      fontWeight: 700,
      lineHeight: '2.125rem', // 34px
      '@media (max-width:640px)': {
        fontSize: '1.375rem', // 22px
        lineHeight: '1.625rem', // 26px
      },
    },
    h5: {
      fontSize: '1.5rem', // 24px
      fontWeight: 600,
      lineHeight: '1.75rem', // 28px
      '@media (max-width:640px)': {
        fontSize: '1.125rem', // 18px
        lineHeight: '1.5rem', // 24px
      },
    },
    'mac-sans-h5': {
      fontFamily: "'Mac Sans', sans-serif",
      fontSize: '1.5rem', // 24px
      fontWeight: 700,
      lineHeight: '1.75rem', // 28px
      '@media (max-width:640px)': {
        fontSize: '1.125rem', // 18px
        lineHeight: '1.5rem', // 24px
      },
    },
    s1: {
      fontSize: '1.125rem', // 18px
      fontWeight: 600,
      lineHeight: '1.75rem', // 28px
      '@media (max-width:640px)': {
        fontSize: '1.0625rem', // 17px
        lineHeight: '1.2rem', // 120% - 19.2px
      },
    },
    s2: {
      fontSize: '1rem', // 16px
      fontWeight: 600,
      lineHeight: '1.5rem', // 24px
    },
    'body-500': {
      fontSize: '1rem', // 16px
      fontWeight: 500,
      lineHeight: '1.5rem', // 24px
    },
    'body-400': {
      fontSize: '1rem', // 16px
      fontWeight: 400,
      lineHeight: '1.5rem', // 24px
    },
    'body-small-500': {
      fontSize: '0.875rem', // 14px
      fontWeight: 500,
      lineHeight: '1.25rem', // 20px
    },
    'body-small-400': {
      fontSize: '0.875rem', // 14px
      fontWeight: 400,
      lineHeight: '1.25rem', // 20px
    },
    'caption-500': {
      fontSize: '0.75rem', // 12px
      fontWeight: 500,
      lineHeight: '1rem', // 16px
    },
    'caption-400': {
      fontSize: '0.75rem', // 12px
      fontWeight: 400,
      lineHeight: '1rem', // 16px
    },
    'caption-small-500': {
      fontSize: '0.875rem', // 10px
      fontWeight: 400,
      lineHeight: '0.875rem', // 14px
    },
    'button-xl': {
      fontSize: '1.125rem', // 18px
      fontWeight: 600,
      lineHeight: '0.875rem', // 14px
    },
    'button-l': {
      fontSize: '1rem', // 16px
      fontWeight: 600,
      lineHeight: '1.25rem', // 20px
    },
    'button-m': {
      fontSize: '0.875rem', // 14px
      fontWeight: 600,
      lineHeight: '1rem', // 16px
    },
    'button-s': {
      fontSize: '0.75rem', // 12px
      fontWeight: 600,
      lineHeight: '1rem', // 16px
    },
    'title-cta': {
      fontSize: '1rem', // 16px
      fontWeight: 600,
      lineHeight: '1.5rem', // 24px - 150%
    },
    'paragraph-big': {
      fontSize: '1rem', // 16px
      fontWeight: 500,
      lineHeight: '1.5rem', // 24px - 150%
    },
    'title-small': {
      fontSize: '0.875rem', // 14px
      fontWeight: 600,
      lineHeight: '1.4rem', // 140%
    },
    placeholder: {
      fontSize: '0.875rem', // 14px
      fontWeight: 500,
      lineHeight: '1.4rem', // 140%
    },
    details: {
      fontSize: '0.75rem', // 12px
      fontWeight: 600,
      lineHeight: '1.3rem', // 20.8px - 130%
    },
    'paragraph-small': {
      fontSize: '0.75rem', // 12px
      fontWeight: 500,
      lineHeight: '1.3rem', // 20.8px - 130%
    },
    'caption-small': {
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: '1.2rem', // 19.2px - 120%
    },
  },

  customShadows: {
    default: '0px 4px 20px rgba(0, 0, 0, 0.5)',
  },
  components: {
    MuiContainer: {
      defaultProps: {
        disableGutters: true,
      },
      styleOverrides: {
        root: ({}) => ({
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }),
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          textTransform: 'none',
          borderRadius: '8px',
          '--variant-containedColor': '#FCFCFC',

          // size
          '&.MuiButton-sizeLarge': {
            padding: '16px 32px',
          },

          '&.MuiButton-sizeMedium': {
            padding: '14px 32px',
          },

          '&.MuiButton-sizeSmall': {
            padding: '10px 32px',
          },

          // color
          '&.MuiButton-containedPrimary': {
            background: theme.palette.primary,
            boxShadow: '0 0 0 5px #9766EC25 !important',
          },

          '&.MuiButton-containedSecondary': {
            background: theme.palette.secondary.main,
            color: theme.palette.secondary.contrastText,
          },

          '&.MuiButton-containedWhite': {
            background: theme.palette.secondary.contrastText,
            color: theme.palette.primary.main,
            boxShadow: '0px 0px 0px 5px rgba(255, 255, 255, 0.20) !important',
          },

          '&.MuiButton-containedPrimary:hover': {
            background: theme.palette.primary.dark,
          },

          '&.MuiButton-containedSecondary:hover': {
            background: theme.palette.secondary.dark,
            color: theme.palette.secondary.contrastText,
          },

          '&.MuiButton-text': {
            color: theme.palette.primary.main,
          },

          // disabled
          '&.MuiButton-contained.Mui-disabled': {
            background: theme.palette.text.disabled,
            color: theme.palette.paper.main,
            boxShadow: 'none !important',
          },

          '&.MuiButton-outlined.Mui-disabled': {
            background: 'transparent !important',
            color: theme.palette.text.disabled,
            border: `1px solid ${theme.palette.text.disabled}`,
          },

          '&.MuiButton-outlinedPrimary': {
            border: `1px solid ${theme.palette.primary.main}`,
          },

          '&.MuiButton-outlinedSecondary': {
            border: `1px solid ${theme.palette.secondary.main}`,
          },
        }),
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.primary.main,
          '&.Mui-disabled': {
            color: theme.palette.text.disabled,
          },
          '&.button-icon-contained': {
            width: '44px',
            height: '44px',
            borderRadius: `${theme.spacing(1)}`,
          },
          '&.button-icon-contained.button-icon-primary': {
            background: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            boxShadow: '0 0 0 5px #9766EC25',
          },
          '&.button-icon-contained.button-icon-secondary': {
            background: theme.palette.secondary.main,
            color: theme.palette.primary.contrastText,
            boxShadow: '0 0 0 5px #9766EC25',
          },
        }),
        colorInherit: ({ theme }) => ({
          borderRadius: theme.spacing(1),
          color: theme.palette.primary.main,
          border: `1px solid ${theme.palette.primary.main}`,
          '&.Mui-disabled': {
            border: `1px solid ${theme.palette.text.disabled}`,
            color: theme.palette.text.disabled,
          },
        }),
      },
    },
    MuiChip: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: theme.spacing(0.75),
          padding: `${theme.spacing(0.25)} ${theme.spacing(0.5)}`,
        }),
        labelSmall: ({ theme }) => ({
          padding: `0px ${theme.spacing(0.5)}`,
        }),
        labelMedium: ({ theme }) => ({
          padding: `3px ${theme.spacing(0.5)}`,
        }),
        avatar: ({ theme }) => ({
          marginLeft: '0px',
        }),
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.primary.main,
        }),
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.title.main,
          fontWeight: 600,

          '&.Mui-error': {
            color: theme.palette.error.main,
          },
        }),
      },
    },
    MuiPagination: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiPaginationItem-root': {
            color: theme.palette.primary.main,
          },
        }),
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: ({ theme }) => ({
          fontSize: '0.75rem',
          color: theme.palette.common.white,
          backgroundColor: '#545668ed',
          borderRadius: theme.spacing(1),
          padding: `${theme.spacing(1.2)} ${theme.spacing(2)}`,
          maxWidth: '180px',
        }),
        arrow: {
          color: '#545668ed',
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: ({ theme }) => ({
          width: 40,
          height: 24,
          borderRadius: 12,
          display: 'flex',
          padding: 0,
          marginLeft: 11,
          marginRight: 8,
          '&:active .MuiSwitch-thumb': {
            width: 15,
          },
          '&:active .Mui-checked': {
            transform: 'translateX(1px)',
          },
          '.Mui-checked .MuiSwitch-thumb': {
            backgroundColor: `${theme.palette.primary.main} !important`,
          },
        }),
        switchBase: ({ theme }) => ({
          padding: '4px',
          '&.Mui-checked': {
            transform: 'translateX(16px)',
            color: '#fff',
            '& + .MuiSwitch-track': {
              opacity: 1,
              backgroundColor: theme.palette.primary.light,
              border: `2px solid ${theme.palette.primary.main}`,
            },
            '& + .MuiSwitch-thumb': {
              backgroundColor: theme.palette.primary.main,
            },
          },
        }),
        thumb: {
          width: 16,
          height: 16,
          borderRadius: 8,
          transition: 'width 250ms',
        },
        track: {
          borderRadius: 12,
          opacity: 1,
          border: '2px solid white',
          boxSizing: 'border-box',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: theme.spacing(2),
          backgroundColor: theme.palette.paper.main,
          '&.MuiPaper-outlined': {
            borderColor: '#8C8D97', // Action Divider
          },
        }),
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: theme.spacing(2),
          //transition: "all 0.3s ease",
          '.MuiCard-root > .MuiCardActionArea-root:hover': {
            transform: 'translateY(-2px) scale(1.01)', // Slight lift and scale effect
            //boxShadow: "0 0px 3px #8C8D97", // Softens the shadow on hover
            //boxShadow: "0 0 0 2px #7934CD",
          },
        }),
      },
    },
    MuiCardActionArea: {
      styleOverrides: {
        root: ({ theme }) => ({
          transition: 'all 0.3s ease',
          '&:hover': {
            backgroundColor: '#8C8D9705',
          },
          '.MuiCardActionArea > .MuiCard': {
            boxShadow: '0 0 0 2px #7934CD',
          },
        }),
      },
    },
    MuiCardMedia: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: theme.spacing(1),
          borderRadius: theme.spacing(2.5),
        }),
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
        }),
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: theme.spacing(2),
          backgroundColor: theme.palette.paper.main,
          border: '1px solid #8C8D97',
          '&.Mui-expanded': {
            margin: '0px',
          },
          '&.filter-accordion': {
            border: 'none',
            boxShadow: 'none',
          },
        }),
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: ({}) => ({
          minHeight: 'auto',
          '&.Mui-expanded': {
            minHeight: 'auto',
          },
        }),
        content: ({}) => ({
          margin: '16px 0px !important',
        }),
      },
    },
    MuiAppBar: {
      styleOverrides: {
        colorPrimary: ({ theme }) => ({
          backgroundColor: theme.palette.paper.main,
          borderRadius: '0px',
          boxShadow: 'none',
          borderBottom: `1px solid ${theme.palette.neutral.main}`,
        }),
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: ({ theme }) => ({
          backgroundColor: theme.palette.background.default,
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          minHeight: theme.spacing(5),
        }),
      },
    },
    MuiTable: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.background.default,
        }),
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderColor: theme.palette.borders.main,
        }),
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.primary.main,
        }),
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.background.default,
        }),
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(32, 33, 39, 0.8)',
          display: 'none',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        root: {
          // Ensure Dialog backdrop is shown by default
          '.MuiBackdrop-root': {
            display: 'block', // Explicitly ensure the backdrop is visible in Dialogs
          },
        },
        paper: ({ theme }) => ({
          backgroundColor: theme.palette.paper.main,
          boxShadow: 'none',
          borderRadius: theme.spacing(2),
          [theme.breakpoints.down('md')]: {
            width: '100% !important',
            height: '100% !important',
            maxHeight: '100% !important',
            margin: '0px !important',
          },
        }),
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.primary,
          padding: theme.spacing(3),
          [theme.breakpoints.down('md')]: {
            padding: theme.spacing(2),
          },
        }),
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.paper.main,
          [theme.breakpoints.down('md')]: {
            padding: theme.spacing(2),
          },
        }),
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: theme.spacing(3),
          borderTop: `1px solid ${theme.palette.neutral.main}`,
          [theme.breakpoints.down('md')]: {
            padding: theme.spacing(2),
          },
        }),
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: ({ theme }: any) => ({
          color: theme.palette.text.primary,

          // Size Small
          '& .MuiInputBase-inputSizeSmall': {
            padding: ` ${theme.spacing(1.5)} ${theme.spacing(2)}`,
            fontSize: '12px !important',
          },

          // Focused
          '& .Mui-focused .MuiOutlinedInput-root': {
            color: theme.palette.text.primary,
            backgroundColor: '#393A47', // Action focus
          },
          '& .Mui-focused .MuiInputLabel-root': {
            color: theme.palette.text.primary,
            marginBottom: theme.spacing(0.5),
          },
          '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#9766EC !important', // primary main
          },
          // Nascondi il notchedOutline
          '& .MuiInputBase-root-MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.text.secondary,
          },
          '& .MuiOutlinedInput-notchedOutline legend': {
            display: 'none !important',
          },
          '.MuiOutlinedInput-notchedOutline': {
            borderColor: '#8C8D97 !important', // Action Divider
            top: '0',
          },
          '& input:-webkit-autofill': {
            WebkitBoxShadow: '0 0 0 100px #1C1D22 inset !important', // Sfondo specifico
            WebkitTextFillColor: '#fff !important', // Colore del testo
            caretColor: '#fff !important',
          },
          '& input:-webkit-autofill:focus': {
            WebkitBoxShadow: '0 0 0 100px #1C1D22 inset !important',
            WebkitTextFillColor: '#fff !important',
            caretColor: '#fff !important',
          },
          '& input[type="password"]:-webkit-autofill': {
            WebkitBoxShadow: '0 0 0 100px #1C1D22 inset !important', // Background color fix for password field
            WebkitTextFillColor: '#fff !important', // Text color inside autofill
            caretColor: '#fff !important',
          },

          '& .Mui-disabled': {
            pointerEvents: 'none',
          },
        }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }: any) => ({
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: `${theme.palette.primary.main} !important`,
          },

          '& input:-webkit-autofill': {
            WebkitBoxShadow: '0 0 0 100px #1C1D22 inset !important', // Sfondo specifico
            WebkitTextFillColor: '#fff !important', // Colore del testo
            caretColor: '#fff !important',
          },

          '&.MuiInputBase-sizeSmall .MuiInputBase-input': {
            padding: `11px ${theme.spacing(2)}`,
            fontSize: '10px !important',
          },

          '&.MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.action.divider,
          },

          // Error
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: `${theme.palette.error.main} !important`,
          },
        }),
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.primary,
          // Input
          '& .MuiInputBase-input': {
            padding: theme.spacing(2),
            fontSize: '16px',
            border: 'none',
            borderRadius: '0px !important',
          },
          '& .MuiInputBase-formControl': {
            color: theme.palette.text.primary,
            border: '1px inset #8C8D97', // Action Divider
            borderRadius: theme.spacing(1),
          },

          '& .MuiInputBase-formControl ::-webkit-input-placeholder': {
            color: theme.palette.text.secondary,
            opacity: '1 !important',
          },

          // Variant Outlined
          '& .MuiOutlinedInput-root': {
            color: theme.palette.text.primary,
            border: 'none',
            backgroundColor: 'transparent !important',
          },

          // Label
          '& .MuiInputLabel-root': {
            color: theme.palette.text.primary,
            position: 'relative',
            transform: 'none',
            marginBottom: '6px',
            fontWeight: '600',
          },

          // Focused
          '& .Mui-focused.MuiOutlinedInput-root': {
            color: theme.palette.text.primary, // text primary
            backgroundColor: '#393A47 !important', // Action focus
          },
          '& .Mui-focused .MuiInputLabel-root': {
            color: (theme.palette.text.primary, '!important'),
            marginBottom: theme.spacing(0.5),
          },
          '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: (theme.palette.primary.main, '!important'),
            top: '0',
          },

          // hide notchedOutline
          '& .MuiOutlinedInput-notchedOutline legend': {
            display: 'none !important',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#8C8D97', // Action Divider
            top: '0',
          },

          // adornment color
          '& .MuiIconButton-root': {
            color: theme.palette.text.secondary,
            padding: '8px 10px 8px 4px',
          },

          // creative checkbox
          '&.creative-checkbox input': {
            position: 'absolute',
            display: 'none',
          },
        }),
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.primary,
          '& .MuiTypography-root': {
            fontWeight: '500',
          },
          // size small
          '& .MuiCheckbox-sizeSmall ~ .MuiTypography-root': {
            fontSize: '14px', // button-m
          },
          // checked
          '& .MuiCheckbox-root.Mui-checked .MuiSvgIcon-root': {
            fill: 'rgba(151, 102, 236, 0.55)',
          },
        }),
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: ({}) => ({
          marginLeft: '0px',
        }),
      },
    },
    MuiMobileStepper: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: '0px',
          color: theme.palette.neutral.main,
          '&.Mui-disabled': {
            color: theme.palette.neutral.main,
          },
        }),
        dot: ({ theme }) => ({
          height: theme.spacing(2),
          width: theme.spacing(2),
          backgroundColor: theme.palette.neutral.main,
          margin: `0 ${theme.spacing(1 / 2)}`,
        }),
        dotActive: ({ theme }) => ({
          backgroundColor: theme.palette.primary.main,
        }),
      },
    },

    MuiStep: {
      styleOverrides: {
        root: ({ theme }) => ({
          [theme.breakpoints.down('sm')]: {
            // Applies styles only for mobile devices
            paddingLeft: '4px !important',
            paddingRight: '4px !important',

            '& .QontoStepIcon-circle': {
              height: '14px !important',
              width: '40px !important',
            },
          },
        }),
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: ({ theme }) => ({
          height: theme.spacing(2),
          borderRadius: theme.spacing(2),
          backgroundColor: theme.palette.neutral.main,
        }),
        bar: ({ theme }) => ({
          borderRadius: theme.spacing(2),
        }),
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.action.divider,
        }),
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: theme.spacing(1),

          '&.MuiAlert-colorError': {
            backgroundColor: '#FF7C7C10',
            border: 'transparent',
            color: '#FF7C7C',
          },
        }),
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          '&.MuiPaper-root.MuiPopover-paper.MuiMenu-paper': {
            boxShadow: 'none',
          },
          // '&.MuiBackdrop-root': {
          //   display: 'none !important',
          // },
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          padding: '3px',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          [theme.breakpoints.up('sm')]: {
            height: '48px',
          },
        }),
      },
    },
    MuiSnackbarContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&.success': {
            backgroundColor: theme.palette.success.light,
            color: theme.palette.success.main,
          },
          '&.error': {
            backgroundColor: theme.palette.error.light,
            color: theme.palette.error.main,
          },
          '&.warning': {
            backgroundColor: theme.palette.warning.light,
            color: theme.palette.warning.main,
          },
          '&.info': {
            backgroundColor: theme.palette.info.light,
            color: theme.palette.warning.main,
          },
        }),
      },
    },
    MuiAvatarGroup: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiAvatar-root': {
            width: theme.spacing(4),
            height: theme.spacing(4),
            fontSize: '1rem',
          },
        }),
      },
    },
  },
});

export default theme;
